import { useState, useRef, DragEvent } from "react";
import Cropper from "react-easy-crop";
import { useTranslations } from "../../../contexts/Translation";
import Button, { LinkButton } from "../../Button/Button";
import Modal from "../../Modal/Modal";

type AspectRatio = "4:3" | "square" | "16:9";

const aspectMap: Record<AspectRatio, number> = {
  "4:3": 4 / 3,
  square: 1,
  "16:9": 16 / 9,
};

type ImageUploaderProps = {
  maxWidth: number;
  maxHeight: number;
  aspectRatios?: AspectRatio[];
  defaultAspect?: AspectRatio;
  original: string | undefined;
  onImageReady: (base64: string, alt?: string) => void;
  onImageFileLoaded?: (base64: string) => void;
  showAltField?: boolean;
  initialAlt?: string;
  quality?: number;
  onClose?: () => void;
};

export default function ImageUploader({
  maxWidth,
  maxHeight,
  aspectRatios = ["4:3", "square", "16:9"],
  defaultAspect = "square",
  onImageReady,
  onImageFileLoaded,
  original,
  showAltField = false,
  initialAlt = "",
  quality = 0.7,
  onClose,
}: ImageUploaderProps) {
  const t = useTranslations("imageUploader");
  const initialAspect = aspectRatios.length === 1 ? aspectMap[aspectRatios[0]] : aspectMap[defaultAspect];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [imageSrc, setImageSrc] = useState<string | undefined>(original);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState<number>(initialAspect);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [dragOver, setDragOver] = useState(false);
  const [alt, setAlt] = useState<string>(initialAlt);

  const onCropComplete = (_: any, croppedPixels: any) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result as string;
      setImageSrc(src);
      if (onImageFileLoaded) {
        onImageFileLoaded(src);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const getCroppedImage = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const image = new Image();
    image.src = imageSrc;
    await new Promise((res) => (image.onload = res));

    const canvas = document.createElement("canvas");
    const scale = Math.min(maxWidth / croppedAreaPixels.width, maxHeight / croppedAreaPixels.height, 1);
    canvas.width = croppedAreaPixels.width * scale;
    canvas.height = croppedAreaPixels.height * scale;

    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(image, croppedAreaPixels.x, croppedAreaPixels.y, croppedAreaPixels.width, croppedAreaPixels.height, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          onImageReady(reader.result as string, showAltField ? alt : undefined);
        };
        reader.readAsDataURL(blob);
      },
      "image/webp",
      quality
    );
  };

  return (
    <Modal
      onClose={() => {
        if (typeof onClose === "function") {
          onClose();
        }
      }}
      title={t("chooseFile")}
    >
      <div
        style={{
          border: "2px dashed #ccc",
          padding: 16,
          textAlign: "center",
          background: dragOver ? "#f0f0f0" : "transparent",
          marginBottom: 12,
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input type='file' accept='image/*' onChange={handleFileChange} ref={inputRef} hidden />
        {imageSrc ? <img src={imageSrc} alt='preview' style={{ maxWidth: "100%", maxHeight: 200 }} /> : t("chooseFile")}
      </div>

      {imageSrc && (
        <>
          <div style={{ position: "relative", width: "100%", height: 400 }}>
            <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
          </div>
          <div style={{ marginTop: 12 }}>
            <label>
              {t("zoomLabel")}:
              <input type='range' min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
            </label>
            {aspectRatios.length > 1 && (
              <label style={{ marginLeft: 16 }}>
                {t("aspectLabel")}:
                <select onChange={(e) => setAspect(aspectMap[e.target.value as AspectRatio])}>
                  {aspectRatios.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {showAltField && (
              <div style={{ marginTop: 12 }}>
                <label>
                  Alt-tekst:
                  <input
                    type='text'
                    value={alt}
                    onChange={(e) => setAlt(e.target.value)}
                    style={{ marginLeft: 8, width: 200 }}
                    placeholder='Beskriv bildet for universell utforming'
                  />
                </label>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <LinkButton
                onClick={() => {
                  if (typeof onClose === "function") {
                    onClose();
                  }
                }}
              >
                {t("cancel")}
              </LinkButton>
              <Button
                type='button'
                onClick={async () => {
                  await getCroppedImage();
                  if (typeof onClose === "function") {
                    onClose();
                  }
                }}
              >
                {t("submitImage")}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
